import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BusinessHour {
  id: string;
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

const DAY_COUNT = 7;

const normalizeTime = (time?: string | null) => (time ? time.slice(0, 5) : '');

export const createDefaultHours = (): BusinessHour[] =>
  Array.from({ length: DAY_COUNT }, (_, day) => ({
    id: '',
    day_of_week: day,
    is_open: day !== 0,
    open_time: '08:00',
    close_time: '18:00',
  }));

export interface OperationalHoursData {
  hours: BusinessHour[];
  bufferMinutes: number;
}

export function useOperationalHoursData(
  orgId: string | null | undefined,
  orgLoading: boolean,
) {
  return useQuery({
    queryKey: ['operational-hours', orgId],
    enabled: Boolean(orgId) && !orgLoading,
    queryFn: async (): Promise<OperationalHoursData> => {
      const [hoursRes, companyRes] = await Promise.all([
        supabase
          .from('business_hours')
          .select('*')
          .eq('organization_id', orgId!)
          .order('day_of_week', { ascending: true }),
        supabase
          .from('company_info')
          .select('id, buffer_minutes')
          .eq('organization_id', orgId!)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (hoursRes.error) throw hoursRes.error;

      const existingHours = (hoursRes.data ?? []) as BusinessHour[];
      const completeHours = createDefaultHours().map((defaultHour) => {
        const existing = existingHours.find((h) => h.day_of_week === defaultHour.day_of_week);
        return existing
          ? {
              ...defaultHour,
              ...existing,
              open_time: normalizeTime(existing.open_time) || defaultHour.open_time,
              close_time: normalizeTime(existing.close_time) || defaultHour.close_time,
            }
          : defaultHour;
      });

      const bufferMinutes =
        companyRes.data?.buffer_minutes != null ? companyRes.data.buffer_minutes : 30;

      return { hours: completeHours, bufferMinutes };
    },
  });
}

export interface SaveOperationalHoursInput {
  orgId: string;
  hours: BusinessHour[];
  bufferMinutes: number;
}

export function useSaveOperationalHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, hours, bufferMinutes }: SaveOperationalHoursInput) => {
      // Save business hours, creating default rows when this org has none yet
      for (const hour of hours) {
        const payload = {
          organization_id: orgId,
          day_of_week: hour.day_of_week,
          is_open: hour.is_open,
          open_time: hour.open_time,
          close_time: hour.close_time,
        };

        const { error } = hour.id
          ? await supabase
              .from('business_hours')
              .update(payload)
              .eq('id', hour.id)
              .eq('organization_id', orgId)
          : await supabase.from('business_hours').insert(payload);

        if (error) throw error;
      }

      // Save buffer minutes to company_info for this org
      const { error: bufferError } = await supabase
        .from('company_info')
        .update({ buffer_minutes: bufferMinutes })
        .eq('organization_id', orgId);

      if (bufferError) throw bufferError;
    },
    onSuccess: (_result, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['operational-hours', orgId] });
    },
  });
}
